import React, { useCallback } from 'react';
import clsx from 'clsx';
import { makeStyles } from 'react-scale/components/design-system/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AnnotationSubstepRows from './ToolTipComponents/AnnotationSubstepRows';
import CheckEveryHeader from './ToolTipComponents/CheckEveryHeader';
import { WorkflowStepActionButtonProps } from './ToolTipComponents/DefaultButtonRow';
import SingleButtonRow from './ToolTipComponents/SingleButtonRow';
import { CheckEveryAnnotationToolTipProps } from '../types';
import DefaultToolTipBody from './ToolTipComponents/DefaultToolTipBody';

const useStyles = makeStyles({
  blankCheckEveryDescription: {
    display: 'flex',
    backgroundColor: '#fbe6d3',
    borderRadius: '8px',
    padding: '8px 10px',
    margin: '12px 16px',
    alignItems: 'center',
    '& tooltipDescription': {
      color: '#E78745',
    },
  },
  tooltipDescription: {
    minHeight: 25,
    overflow: 'auto',
    padding: '0px 8px',

    // Base values are smaller.
    fontSize: '11px',
    lineHeight: '16px',
    fontWeight: 400,
    '&.isCompleted': {
      opacity: 0.3,
    },
  },
  instructionPanelLink: {
    display: 'flex',
    fontSize: '11px',
    lineHeight: '16px',
    fontWeight: 'bold',
    padding: '6px 8px',
    alignItems: 'center',
    color: 'rgba(22, 122, 251, 1)',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  buttonRow: {
    minHeight: 50,
    background: 'var(--color-RemoGray01)',
    padding: 8,
    display: 'flex',
    justifyContent: 'space-evenly',
    alignItems: 'center',
  },
  actionButton: {
    '& span': {
      fontSize: '11px',
    },
  },
  iconToggle: {
    cursor: 'pointer',
    fontSize: '16px',
  },
});

const AnnotationToolTip: React.FC<CheckEveryAnnotationToolTipProps> = props => {
  const classes = useStyles();

  const {
    workflowState,
    setLockedCheckEveryAnnotation,
    setActiveCheckEveryAnnotation,
    toggleCompleted,
    completeAndMoveNext,
  } = props.workflowHook;

  const { setSelectedAnnotationIds } = props.labelerStateHook;
  const { setAnnotationReviewStateByID } = props.reviewStateHook || {};

  const { activeCheckEveryAnnotation, currentStep, lockedCheckEveryAnnotation } = workflowState;
  const isCompleted = workflowState.completedStepIds.includes(workflowState.activeStepId);

  const clickStartAnnotationReview = useCallback(() => {
    // Should Always exist because we are on an annotation ToolTip
    if (isCompleted) {
      toggleCompleted(currentStep.id);
      if (workflowState.activeCheckEveryAnnotation) {
        setAnnotationReviewStateByID?.(workflowState.activeCheckEveryAnnotation, false);
      }
    }
    setLockedCheckEveryAnnotation(currentStep.linkedAnnotationId || '');
  }, [
    currentStep,
    isCompleted,
    setLockedCheckEveryAnnotation,
    toggleCompleted,
    setAnnotationReviewStateByID,
    workflowState.activeCheckEveryAnnotation,
  ]);

  const clickCompleteAnnotationReview = useCallback(() => {
    if (workflowState.activeCheckEveryAnnotation) {
      setAnnotationReviewStateByID?.(workflowState.activeCheckEveryAnnotation, true);
    }
    setLockedCheckEveryAnnotation(null);
    setActiveCheckEveryAnnotation(null);
    setSelectedAnnotationIds(new Set());
    completeAndMoveNext();
  }, [
    completeAndMoveNext,
    setActiveCheckEveryAnnotation,
    setLockedCheckEveryAnnotation,
    setSelectedAnnotationIds,
    setAnnotationReviewStateByID,
    workflowState.activeCheckEveryAnnotation,
  ]);

  const annotationIsLocked = !!lockedCheckEveryAnnotation;
  const annotationHasSubsteps = currentStep.steps && currentStep.steps?.length > 0;

  const getBlankCheckEveryDescription = () => {
    return (
      <div className={classes.blankCheckEveryDescription}>
        <FontAwesomeIcon icon="exclamation-triangle" color="#F19637" style={{ fontSize: '20px' }} />
        <div className={classes.tooltipDescription}>
          Select annotation to check in the side bar or canvas
        </div>
      </div>
    );
  };

  const getBasicAnnotationDescription = () => {
    const uncompletedText = annotationIsLocked ? 'Complete checking' : 'Begin checking';
    const uncompletedStateClick = annotationIsLocked
      ? clickCompleteAnnotationReview
      : clickStartAnnotationReview;

    const actionButtonProps: WorkflowStepActionButtonProps = {
      completedText: 'Restart Annotation Review',
      completedStateClick: clickStartAnnotationReview,
      startedText: uncompletedText,
      startedStateClick: clickCompleteAnnotationReview,
      allChildrenCompletedText: uncompletedText,
      allChildrenCompletedStateClick: clickCompleteAnnotationReview,
      uncompletedText,
      uncompletedStateClick,
    };

    return (
      <>
        {annotationIsLocked ? (
          <DefaultToolTipBody workflowHook={props.workflowHook} />
        ) : (
          <div className={clsx(classes.tooltipDescription, { isCompleted })}> </div>
        )}
        <SingleButtonRow
          workflowHook={props.workflowHook}
          actionButtonProps={actionButtonProps}
          reviewStateHook={props.reviewStateHook}
        />
      </>
    );
  };

  const getAnnotationWithSubstepDescription = () => {
    if (!currentStep.steps) {
      // Should never happen, this means error in checking if substeps exist
      return getBasicAnnotationDescription();
    }

    const actionButtonProps: WorkflowStepActionButtonProps = {
      completedText: 'Redo Annotation',
      completedStateClick: clickCompleteAnnotationReview,
      startedText: 'Complete checking',
      startedStateClick: clickCompleteAnnotationReview,
      allChildrenCompletedText: 'Complete checking',
      allChildrenCompletedStateClick: clickCompleteAnnotationReview,
      uncompletedText: 'Complete checking',
      uncompletedStateClick: clickCompleteAnnotationReview,
    };

    return (
      <>
        <AnnotationSubstepRows labelerStateHook={props.labelerStateHook} />
        <SingleButtonRow
          workflowHook={props.workflowHook}
          actionButtonProps={actionButtonProps}
          reviewStateHook={props.reviewStateHook}
        />
      </>
    );
  };

  return (
    <div>
      <CheckEveryHeader
        workflowHook={props.workflowHook}
        labelerStateHook={props.labelerStateHook}
      />
      {lockedCheckEveryAnnotation || activeCheckEveryAnnotation
        ? annotationHasSubsteps && lockedCheckEveryAnnotation
          ? getAnnotationWithSubstepDescription()
          : getBasicAnnotationDescription()
        : getBlankCheckEveryDescription()}
    </div>
  );
};

export default React.memo(AnnotationToolTip);
